/* AppHeader — top navigation bar with brand logo, section nav dropdown,
   DB download/upload actions, and user/logout.
   Ported from lib/shell.jsx (claude_design project 5c0836b6-facf-49dd-9c78-62afe3f7bc12).
   Note: the design's "Design System.html" menu link is intentionally omitted (prototype-only). */

import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { saveAs } from 'file-saver';
import axios from 'axios';

import { Icon } from '../Icon';
import { Button } from '../Button';
import { Avatar } from '../Avatar';
import { Dropdown } from '../Dropdown';
import { MenuItem } from '../Dropdown';
import { SECTIONS, getSectionByPath } from './sections';
import { useLazyDownloadDatabaseQuery } from 'src/store/interviewApi';
import { selectUser, selectAccessToken } from 'src/store/userSlice';

function Logo({ size = 30 }: { size?: number }) {
  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: size * 0.3,
        background: 'var(--accent)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'var(--text-on-accent)',
        flexShrink: 0,
      }}
    >
      <Icon name="heart" size={size * 0.55} />
    </div>
  );
}

export function AppHeader() {
  const navigate = useNavigate();
  const location = useLocation();
  const user = useSelector(selectUser);
  const accessToken = useSelector(selectAccessToken);

  const [downloadDb, downloadDbStatus] = useLazyDownloadDatabaseQuery();
  const [dbState, setDbState] = useState<'down' | 'up' | null>(null);

  const currentSection = getSectionByPath(location.pathname);

  const onDownloadDb = async () => {
    setDbState('down');
    try {
      const f = await downloadDb().unwrap();
      const blob = new Blob([JSON.stringify(f)], {
        type: 'text/plain;charset=utf-8',
      });
      saveAs(blob, 'lovellect_db.json');
    } finally {
      setDbState(null);
    }
  };

  const onUploadDb = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json';
    input.click();

    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;
      setDbState('up');
      const payload = new FormData();
      payload.append('file', file);
      const headers: Record<string, string> = {
        Authorization: `Bearer ${accessToken}`,
      };
      try {
        await axios.post(
          `${process.env.REACT_APP_BACKEND_BASE_URL}/interviews/upload`,
          payload,
          { headers }
        );
      } finally {
        setDbState(null);
      }
    };
  };

  const onLogout = () => {
    navigate('/logout');
  };

  return (
    <header
      style={{
        height: 'var(--header-h)',
        borderBottom: '1px solid var(--border)',
        background: 'var(--bg-canvas)',
        display: 'flex',
        alignItems: 'center',
        padding: '0 18px',
        gap: 16,
        flexShrink: 0,
        position: 'relative',
        zIndex: 50,
      }}
    >
      {/* Left: brand + nav dropdown */}
      <div className="row gap-3" style={{ minWidth: 0 }}>
        <Logo />
        <Dropdown
          width={248}
          align="left"
          trigger={
            <button
              className="btn btn-ghost"
              style={{ height: 40, paddingLeft: 10, paddingRight: 10, gap: 9 }}
            >
              <div style={{ textAlign: 'left' }}>
                <div
                  className="eyebrow"
                  style={{ fontSize: 9, lineHeight: 1.1 }}
                >
                  Lovellect Admin
                </div>
                <div
                  style={{
                    fontSize: 'var(--fs-md)',
                    fontWeight: 600,
                    letterSpacing: '-0.01em',
                    color: 'var(--text)',
                    textTransform: 'uppercase',
                  }}
                >
                  {currentSection?.label ?? 'Navigate'}
                </div>
              </div>
              <Icon
                name="chevrons-up-down"
                size={15}
                style={{ color: 'var(--text-muted)' }}
              />
            </button>
          }
        >
          <div className="eyebrow" style={{ padding: '6px 9px 4px' }}>
            Sections
          </div>
          {SECTIONS.map((s) => (
            <MenuItem
              key={s.id}
              icon={s.icon}
              active={s.id === currentSection?.id}
              onClick={() => navigate(s.path)}
            >
              {s.label}
            </MenuItem>
          ))}
        </Dropdown>
      </div>

      {/* DB actions (left-aligned, next to nav) */}
      <div className="row gap-2" style={{ marginRight: 'auto' }}>
        <Button
          variant="secondary"
          size="sm"
          leftIcon="download"
          loading={dbState === 'down' || downloadDbStatus.isLoading}
          onClick={onDownloadDb}
        >
          Download Database
        </Button>
        <Button
          variant="secondary"
          size="sm"
          leftIcon="upload"
          loading={dbState === 'up'}
          onClick={onUploadDb}
        >
          Upload Database
        </Button>
      </div>

      {/* Right: user + logout */}
      <div className="row gap-2">
        <div className="row gap-2" style={{ paddingLeft: 4 }}>
          <Avatar name={user?.email ?? 'Admin'} size={28} hue={350} />
          <span className="text-secondary" style={{ fontSize: 'var(--fs-sm)' }}>
            {user?.email ?? ''}
          </span>
        </div>
        <Button variant="ghost" size="sm" leftIcon="log-out" onClick={onLogout}>
          Logout
        </Button>
      </div>
    </header>
  );
}

export default AppHeader;
